import { useTranslation } from "react-i18next";
import { YesNoAnswer } from "src/types";
import {
  usePhoneNumberSchema,
  ystr,
  ystrAccidentCircumstance,
  ystrRequired,
  ystrRequiredInjuredType,
  ystrRequiredVehicleType,
  ystrRequiredYesNo,
  yupDate,
  yupDateRequired,
  yupEmail,
  yupEmailRequired,
  yupHour,
} from "src/utils";
import * as Yup from "yup";

const useAccidentCircumstancesValidationSchema = () => {
  const schema = Yup.object().shape({
    selectedCircumstance: ystrAccidentCircumstance,
    vehicleType: ystrRequiredVehicleType,
  });

  return schema;
};

const useAccidentInjuredValidationSchema = () => {
  const phoneSchema = usePhoneNumberSchema({ isRequired: false });
  const schema = Yup.object().shape({
    type: ystrRequiredInjuredType,
    name: ystr,
    surname: ystr,
    dateOfBirth: yupDate,
    placeOfBirth: ystr,
    fiscalCode: ystr,
    address: ystr,
    email: yupEmail,
    phoneNumber: phoneSchema,
    emergencyRoom: ystrRequiredYesNo,
    emergencyRoomName: ystr,
    emergencyRoomMunicipality: ystr,
  });

  return schema;
};

const useAccidentInjuredsInfoValidationSchema = () => {
  const injuredSchema = useAccidentInjuredValidationSchema();

  const schema = Yup.object().shape({
    injuredQuestion: ystrRequiredYesNo,
    injuredList: Yup.array().of(injuredSchema),
  });

  return schema;
};

const useAccidentWitnessValidationSchema = () => {
  const phoneSchema = usePhoneNumberSchema({ isRequired: true });
  const schema = Yup.object().shape({
    name: ystrRequired,
    surname: ystrRequired,
    dateOfBirth: yupDateRequired,
    placeOfBirth: ystrRequired,
    fiscalCode: ystr,
    address: ystrRequired,
    email: yupEmailRequired,
    phoneNumber: phoneSchema,
  });

  return schema;
};

const useAccidentGeneralInfoValidationSchema = () => {
  const schema = Yup.object().shape({
    accidentDate: yupDateRequired,
    accidentHour: yupHour,
    accidentLocation: ystrRequired,
  });

  return schema;
};

const useWitnessesInfoValidationSchema = () => {
  const witnessSchema = useAccidentWitnessValidationSchema();
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    witnessesList: Yup.array().of(witnessSchema),
    authorityIntervention: ystrRequiredYesNo,
    authority: ystr.when("authorityIntervention", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.YES,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return schema;
};

const useMaterialDamagesValidationSchema = () => {
  const schema = Yup.object().shape({
    otherVehiclesDamaged: ystrRequiredYesNo,
    otherObjectsDamaged: ystrRequiredYesNo,
    otherVehiclesDamageDetails: ystr,
    otherObjectsDamageDetails: ystr,
  });

  return schema;
};

export const useAccidentValidationSchema = () => {
  const generalInfoSchema = useAccidentGeneralInfoValidationSchema();
  const injuredsInfoSchema = useAccidentInjuredsInfoValidationSchema();
  const materialDamagesSchema = useMaterialDamagesValidationSchema();
  const witnessesInfoSchema = useWitnessesInfoValidationSchema();
  const circumstancesSchema = useAccidentCircumstancesValidationSchema();

  const schema = Yup.object().shape({
    generalInfo: generalInfoSchema,
    injuredsInfo: injuredsInfoSchema,
    materialDamages: materialDamagesSchema,
    witnessesInfo: witnessesInfoSchema,
    vehicleAccidentCircumstances: Yup.array().of(circumstancesSchema),
  });

  return schema;
};
